import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchGetCenterCoachRanking, CenterCoachRankingTrainer } from '../../services/useFeedDashboardQueryService';
import type { JournalWriteBreakdown } from '../../services/useFeedDashboardQueryService';
import type { CenterCoachMetricKey } from '../../api/feedDashboard';
import CoachMetricEvidenceModal from './CoachMetricEvidenceModal';
import { RE_REGISTRATION_SUCCESS_NAMES, buildMeasuredRanking } from './coachPerformanceModel';

// ── 테마 ──────────────────────────────────────────────────────────────────
const T = {
    card: '#FFFFFF',
    cardBorder: '#E2E8F0',
    cardShadow: '0 1px 3px rgba(26,48,93,0.06)',
    navy: '#1A305D',
    blue: '#3D73DD',
    blueBg: '#ECF1FC',
    blueBorder: '#C3D4F4',
    gold: '#C4963C',
    text1: '#1A305D',
    text2: '#5E7093',
    text3: '#8896B0',
    text4: '#A5B1C5',
    border: '#E2E8F0',
    borderLight: '#F0F3F7',
    success: '#00A793',
    successBg: '#E5F8F3',
    inputBg: '#F8FAFC',
};

const COLORS = ['#3D73DD', '#00A793', '#C4963C', '#9E76F3', '#FF8777', '#17B5CB', '#FD8FE5', '#648FE4'];

// ── 카테고리 정의 ──────────────────────────────────────────────────────────
type MetricUnit = 'money' | 'num' | 'pct';
interface MetricConfig {
    /** 백엔드 근거 API(centerCoachEvidence.service.ts)의 metricKey 와 동일해야 한다 */
    key: CenterCoachMetricKey;
    label: string;
    unit: MetricUnit;
    periodFilterable: boolean;
    monthOnly?: boolean; // 월 단위 baseDate 만 노출 (granularity 토글 숨김)
}
interface RankCategory {
    cat: string;
    color: string;
    metrics: MetricConfig[];
}

export const COACH_RANK_CATEGORIES: RankCategory[] = [
    {
        cat: '매출',
        color: T.blue,
        metrics: [
            { key: 'revenue', label: '매출액', unit: 'money', periodFilterable: true },
            { key: 'undeliveredRevenue', label: '미수업금액(부채)', unit: 'money', periodFilterable: false },
            { key: 'avgPricePerClass', label: '평균 회당단가', unit: 'money', periodFilterable: true },
        ],
    },
    {
        cat: '수업',
        color: T.success,
        metrics: [
            { key: 'totalClasses', label: '진행한 수업수', unit: 'num', periodFilterable: true },
            { key: 'noShowCount', label: '노쇼수', unit: 'num', periodFilterable: true },
            { key: 'sameDayCancelCount', label: '당일취소', unit: 'num', periodFilterable: true },
            { key: 'sameDayChangeCount', label: '당일변경', unit: 'num', periodFilterable: true },
            { key: 'journalWriteRate', label: '일지 작성률', unit: 'pct', periodFilterable: true },
            { key: 'reviewAvg', label: '리뷰평점', unit: 'num', periodFilterable: false },
        ],
    },
    {
        cat: '결제',
        color: T.gold,
        metrics: [
            { key: 'totalMembers', label: '활성 회원', unit: 'num', periodFilterable: false, monthOnly: true },
            { key: 'newPayMembers', label: '신규결제', unit: 'num', periodFilterable: true },
            { key: 'reRegPayMembers', label: '재등록결제', unit: 'num', periodFilterable: true },
            { key: 'reRegistrationRate', label: '재등록률', unit: 'pct', periodFilterable: false, monthOnly: true },
            { key: 'reRegistrationSuccessRate', label: '재등록 성공률', unit: 'pct', periodFilterable: false, monthOnly: true },
        ],
    },
];

// ── 데이터 평탄화 ─────────────────────────────────────────────────────────
type FlatTrainer = {
    coachId: string;
    name: string;
    revenue: number;
    prevRevenue: number;
    totalClasses: number;
    noShowCount: number;
    sameDayCancelCount: number;
    sameDayChangeCount: number;
    journalCount: number;
    avgPricePerClass: number | null;
    journalWriteRate: number | null;
    newPayMembers: number;
    newPayRevenue: number;
    reRegPayMembers: number;
    reRegPayRevenue: number;
    totalMembers: number;
    undeliveredRevenue: number;
    reviewAvg: number;
    reRegistrationRate: number | null;
    reRegisteredMemberCount: number;
    activeMemberCount: number;
    reRegistrationSuccessRate?: number | null;
    reRegistrationSucceededMemberCount?: number;
    reRegistrationEndedMemberCount?: number;
    reRegistrationNotYetMemberCount?: number;
    reRegistrationSuccessDataQuality?: 'EXACT' | 'ESTIMATED' | 'MIXED';
    [key: string]: any;
};

export const flattenTrainer = (t: CenterCoachRankingTrainer): FlatTrainer => ({
    coachId: t.coachId,
    name: t.name,
    ...t.periodFiltered,
    ...t.snapshot,
});

export const formatJournalWriteBreakdownTooltip = (
    breakdown: JournalWriteBreakdown,
    displayValue: string | number,
) =>
    `PT ${breakdown.pt?.written ?? 0}/${breakdown.pt?.target ?? 0} · 그룹 ${breakdown.group?.written ?? 0}/${breakdown.group?.target ?? 0} = ${displayValue}`;

// ── 포맷 헬퍼 ─────────────────────────────────────────────────────────────
const fmt = (n: number) => {
    const v = n ?? 0;
    if (v >= 100000000) return (Math.floor((v / 100000000) * 10) / 10).toFixed(1) + '억';
    if (v >= 10000) return (v / 10000).toLocaleString('ko-KR', { maximumFractionDigits: 0 }) + '만';
    return v.toLocaleString('ko-KR');
};

const fmtVal = (v: number | null, unit?: string, key?: string) => {
    if (v == null) return '-';
    if (key === 'reviewAvg') return v ? `★ ${v.toFixed(1)}` : '-';
    if (unit === 'pct') return `${v.toFixed ? v.toFixed(1) : v}%`;
    if (key === 'avgPricePerClass') return v ? `${(v ?? 0).toLocaleString('ko-KR')}원` : '-';
    if (unit === 'money') return fmt(v);
    return v;
};

// ── 기간 유틸 ─────────────────────────────────────────────────────────────
const PERIODS = [
    { key: 'yearly', label: '연별' },
    { key: 'monthly', label: '월별' },
    { key: 'weekly', label: '주별' },
    { key: 'daily', label: '일별' },
];

const baseDateToParam = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
};

const shiftBaseDate = (current: Date, period: string, direction: -1 | 1): Date => {
    const d = new Date(current);
    if (period === 'yearly') d.setFullYear(d.getFullYear() + direction);
    else d.setMonth(d.getMonth() + direction);
    return d;
};

const monthsBetween = (from: Date, to: Date): number =>
    (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());

// 근거 API 는 월 단위 집계만 제공한다(백엔드 assertMonthInRange: 최근 24개월, 미래 불가).
const EVIDENCE_MAX_LOOKBACK_MONTHS = 24;

const isSameMonth = (d: Date): boolean => {
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
};

const getWeeksOfMonth = (year: number, month: number): { week: number; start: Date; end: Date; label: string }[] => {
    const weeks: { week: number; start: Date; end: Date; label: string }[] = [];
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    let cursor = new Date(firstDay);
    let weekNum = 1;
    while (cursor <= lastDay) {
        const start = new Date(cursor);
        const daysToSat = 6 - cursor.getDay();
        const end = new Date(cursor);
        end.setDate(cursor.getDate() + daysToSat);
        if (end > lastDay) end.setTime(lastDay.getTime());
        const label = `${weekNum}주(${start.getMonth() + 1}/${start.getDate()}~${end.getMonth() + 1}/${end.getDate()})`;
        weeks.push({ week: weekNum, start, end, label });
        weekNum++;
        cursor = new Date(end);
        cursor.setDate(cursor.getDate() + 1);
    }
    return weeks;
};

// ── RankBadge ────────────────────────────────────────────────────────────
const RankBadge = ({ rank }: { rank: number }) => {
    const styles: Record<number, { bg: string; color: string }> = {
        1: { bg: 'linear-gradient(135deg,#C4963C,#A67B2B)', color: '#fff' },
        2: { bg: 'linear-gradient(135deg,#8896B0,#5E7093)', color: '#fff' },
        3: { bg: 'linear-gradient(135deg,#C4863C,#8B6028)', color: '#fff' },
    };
    const s = styles[rank];
    if (!s)
        return (
            <span style={{ color: T.text3, fontSize: 13, fontWeight: 500, width: 28, textAlign: 'center', display: 'inline-block' }}>
                {rank}
            </span>
        );
    return (
        <span
            style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: '50%', fontSize: 13, fontWeight: 800,
                background: s.bg, color: s.color, boxShadow: '0 2px 6px rgba(196,150,60,0.25)',
            }}
        >
            {rank}
        </span>
    );
};

// ── PeriodToggle ─────────────────────────────────────────────────────────
const navBtnStyle: React.CSSProperties = {
    background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 5,
    cursor: 'pointer', padding: '4px 8px', fontSize: 14, color: T.text2, fontWeight: 700, lineHeight: 1,
};

const PeriodToggle = ({
    value,
    onChange,
    loading,
    baseDate,
    onBaseDateChange,
    monthOnly,
}: {
    value: string;
    onChange: (v: string) => void;
    loading?: boolean;
    baseDate: Date;
    onBaseDateChange: (d: Date) => void;
    monthOnly?: boolean;
}) => {
    // monthOnly 일 땐 baseDate 가 어떤 period 였든 항상 월 단위로 ◀▶ 동작
    const effectiveValue = monthOnly ? 'monthly' : value;
    const isCurrent = effectiveValue === 'yearly'
        ? baseDate.getFullYear() === new Date().getFullYear()
        : isSameMonth(baseDate);
    const label = effectiveValue === 'yearly'
        ? `${baseDate.getFullYear()}년`
        : `${baseDate.getFullYear()}년 ${baseDate.getMonth() + 1}월`;

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', minWidth: 160 }}>
                {!isCurrent ? (
                    <button onClick={() => onBaseDateChange(new Date())} style={{ ...navBtnStyle, fontSize: 13, padding: '2px 8px', color: T.blue, borderColor: T.blueBorder, marginRight: 2 }}>
                        오늘
                    </button>
                ) : <div style={{ width: 34 }} />}
                <button style={navBtnStyle} onClick={() => onBaseDateChange(shiftBaseDate(baseDate, effectiveValue, -1))}>◀</button>
                <span style={{ fontSize: 14, color: isCurrent ? T.text3 : T.blue, fontWeight: 600, minWidth: 90, textAlign: 'center' }}>{label}</span>
                <button style={{ ...navBtnStyle, opacity: isCurrent ? 0.3 : 1 }} disabled={isCurrent} onClick={() => onBaseDateChange(shiftBaseDate(baseDate, effectiveValue, 1))}>▶</button>
            </div>
            {!monthOnly && (
                <div style={{ display: 'flex', gap: 2, background: T.inputBg, borderRadius: 8, padding: 2, border: `1px solid ${T.border}` }}>
                    {PERIODS.map((p) => {
                        const isActive = value === p.key;
                        const isLoading = isActive && loading;
                        return (
                            <button
                                key={p.key}
                                onClick={() => onChange(p.key)}
                                style={{
                                    padding: '7px 18px', borderRadius: 7, border: 'none', cursor: 'pointer',
                                    fontSize: 14, fontWeight: 600,
                                    background: isActive ? T.card : 'transparent',
                                    color: isActive ? T.navy : T.text3,
                                    boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                                    display: 'flex', alignItems: 'center', gap: 5,
                                }}
                            >
                                {isLoading && (
                                    <div style={{
                                        width: 10, height: 10,
                                        border: `1.5px solid ${T.blueBorder}`, borderTopColor: T.blue,
                                        borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0,
                                    }} />
                                )}
                                {p.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────
type Props = {
    centerId: string;
};

const FeedCenterStatisticsRanking = ({ centerId }: Props) => {
    const navigate = useNavigate();

    const [period, setPeriod] = useState<'yearly' | 'monthly' | 'weekly' | 'daily'>('monthly');
    const [baseDate, setBaseDate] = useState<Date>(new Date());
    const [selectedWeek, setSelectedWeek] = useState<number | undefined>(undefined);
    const [selectedDay, setSelectedDay] = useState<number | undefined>(undefined);
    const [activeCat, setActiveCat] = useState(COACH_RANK_CATEGORIES[0].cat);
    const [activeMetric, setActiveMetric] = useState(COACH_RANK_CATEGORIES[0].metrics[0].key);
    const [expanded, setExpanded] = useState(false);
    const [hoveredCoachId, setHoveredCoachId] = useState<string | null>(null);
    const [evidenceTarget, setEvidenceTarget] = useState<{ coachId: string; name: string } | null>(null);

    const baseDateParam = baseDateToParam(baseDate);

    const { data: rankingResp, isFetching } = useQuery({
        ...fetchGetCenterCoachRanking({
            centerId,
            period,
            baseDate: baseDateParam,
            week: selectedWeek,
            day: selectedDay,
        }),
        enabled: !!centerId,
        staleTime: 60 * 1000,
    });

    const trainers: FlatTrainer[] = useMemo(() => {
        const raw: CenterCoachRankingTrainer[] = rankingResp?.data?.data?.trainers ?? [];
        return raw.map(flattenTrainer);
    }, [rankingResp]);

    /**
     * 🔴 이 달 재등록 성공률 **집계가 실패했는가.**
     *
     * 서버는 원천 조회가 실패하면 `degraded` 에 `'reRegSuccess'` 를 넣고, 값은
     * `null`·분자/분모는 `0` 으로 내린다(`superAdminDashboard.service`).
     * 그 모양을 그대로 그리면 「0명 중 0명 · -」 강사 전원에게 금·은·동 배지가 붙는다.
     * 아무도 재지 않은 순위라, 원장이 그 줄을 근거로 면담을 연다.
     */
    const conversionDegraded = useMemo(
        () => (rankingResp?.data?.data?.degraded ?? []).includes('reRegSuccess'),
        [rankingResp]
    );

    const currentCat = COACH_RANK_CATEGORIES.find((c) => c.cat === activeCat)!;
    const currentMetricConf = currentCat.metrics.find((m) => m.key === activeMetric)!;
    const isPeriodFilterable = currentMetricConf?.periodFilterable ?? false;
    const isMonthOnly = currentMetricConf?.monthOnly ?? false;

    const handleCatChange = (cat: string) => {
        setActiveCat(cat);
        const first = COACH_RANK_CATEGORIES.find((c) => c.cat === cat)?.metrics[0];
        if (first) setActiveMetric(first.key);
    };

    const handlePeriodChange = (v: string) => {
        setPeriod(v as any);
        setSelectedWeek(undefined);
        setSelectedDay(undefined);
    };

    /**
     * 순위 한 벌 — 🔴 **등수와 화면 순서를 같은 함수로 만든다.**
     *
     * 예전에는 배지는 `buildMeasuredRanking`, 줄 세우기는 `(value ?? 0)` 내림차순으로
     * **따로** 만들었다. 그러면 값이 없는 강사가 실제 0% 강사보다 위에 서고,
     * 이 목록은 **상위 5개만 보여준 뒤 접기** 때문에 미측정 5명 뒤에 있던
     * **실제 1위가 접힌 목록 밖으로 밀려 아예 안 보인다.**
     *
     * 순서는 `trainers`(서버 순서)로 한 번만 세고, 정렬·배지·접기 상한이 전부
     * 그 결과를 쓴다.
     */
    const metricRanking = useMemo(() => {
        if (conversionDegraded && activeMetric === 'reRegistrationSuccessRate') {
            return new Map<string, number>();
        }
        return buildMeasuredRanking(
            trainers.map((t) => {
                const v = t[activeMetric];
                return { coachId: t.coachId, value: typeof v === 'number' ? v : null };
            })
        );
    }, [trainers, activeMetric, conversionDegraded]);

    /* 측정된 행이 등수 순으로 먼저, 미측정은 맨 뒤(서버 순서 유지) */
    const sorted = useMemo(
        () =>
            [...trainers].sort((a, b) => {
                const ra = metricRanking.get(a.coachId);
                const rb = metricRanking.get(b.coachId);
                if (ra != null && rb != null) return ra - rb;
                if (ra != null) return -1;
                if (rb != null) return 1;
                return 0;
            }),
        [trainers, metricRanking]
    );
    const displayed = expanded ? sorted : sorted.slice(0, 5);
    /*
     * 막대 길이의 기준 — 값 내림차순 정렬이라 첫 줄이 최댓값이다.
     * 기준이 흔들려도 막대가 화면 밖으로 뻗지 않게 렌더에서 100% 로 자른다.
     */
    const maxV = useMemo(() => {
        const values = sorted
            .map((t) => t[activeMetric])
            .filter((v): v is number => typeof v === 'number' && !Number.isNaN(v) && v > 0);
        return values.length ? Math.max(...values) : 1;
    }, [sorted, activeMetric]);

    const weeks = period === 'weekly' ? getWeeksOfMonth(baseDate.getFullYear(), baseDate.getMonth() + 1) : [];
    const daysInMonth = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0).getDate();

    // ── 원천 데이터(근거) 열람 가능 여부 ──
    // 근거는 월 단위로만 집계되므로, 기간 필터가 걸린 지표를 주/일/연으로 보고 있으면
    // 막대 값과 근거 목록의 기간이 어긋난다 → 이때는 열지 않고 이유를 알려준다.
    const monthsAgo = monthsBetween(baseDate, new Date());
    const evidenceBlockReason: string | null = (() => {
        if (isPeriodFilterable && period !== 'monthly') {
            return '원천 데이터는 월 단위로만 볼 수 있어요. 기간을 "월별"로 바꿔주세요.';
        }
        if (monthsAgo < 0 || monthsAgo > EVIDENCE_MAX_LOOKBACK_MONTHS) {
            return `원천 데이터는 최근 ${EVIDENCE_MAX_LOOKBACK_MONTHS}개월까지만 볼 수 있어요.`;
        }
        return null;
    })();
    const canOpenEvidence = evidenceBlockReason === null;

    // 비율 지표는 막대 위에 분자/분모를 함께 보여줘 "무엇을 클릭하면 되는지" 감을 준다.
    const ratioOf = (item: FlatTrainer): string | null => {
        if (activeMetric === 'journalWriteRate') {
            const target = item.journalWriteTarget;
            if (target == null) return null;
            return `${item.journalWriteWritten ?? 0}/${target}`;
        }
        if (activeMetric === 'reRegistrationRate') {
            return `${item.reRegisteredMemberCount ?? 0}/${item.activeMemberCount ?? 0}`;
        }
        if (activeMetric === 'reRegistrationSuccessRate') {
            /*
             * 🔴 분모가 0 이면 적지 않는다. 「0명 중 0명」은 근거가 아니고,
             *    집계가 실패한 달에도 서버가 0/0 을 보내서 「아무도 안 했다」로 읽힌다.
             */
            const ended = item.reRegistrationEndedMemberCount;
            if (ended == null || ended === 0) return null;
            /*
             * 🔴 「N명 중 M명 · X%」 — 분모를 말로 적는다.
             *    `30/63` 은 어느 쪽이 분모인지 한눈에 안 잡히고,
             *    3명 중 1명과 300명 중 100명이 같은 33% 로 읽힌다.
             */
            return `${ended}명 중 ${item.reRegistrationSucceededMemberCount ?? 0}명 · `;
        }
        return null;
    };

    return (
        <div style={{ background: T.card, borderRadius: 21, padding: '24px 28px', border: `1px solid ${T.cardBorder}`, borderTop: `3px solid ${T.success}`, boxShadow: T.cardShadow, width: '100%' }}>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

            {/* 헤더 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                        <circle cx="11" cy="8" r="4" stroke={T.success} strokeWidth="1.8" fill="none" />
                        <path d="M4 19c0-3.5 3.1-6 7-6s7 2.5 7 6" stroke={T.success} strokeWidth="1.8" fill="none" strokeLinecap="round" />
                    </svg>
                    <span style={{ fontSize: 18, fontWeight: 700, color: T.navy }}>지표별 강사 순위</span>
                    {isPeriodFilterable ? (
                        <span style={{ fontSize: 13, color: T.blue, fontWeight: 600, background: T.blueBg, padding: '3px 10px', borderRadius: 6 }}>
                            {period === 'yearly' ? `${baseDate.getFullYear()}년 · 연간` : `${baseDate.getFullYear()}년 ${baseDate.getMonth() + 1}월 · ${{ monthly: '월간', weekly: '주간', daily: '일간' }[period] ?? '월간'}`}
                            {period === 'weekly' && selectedWeek != null && ` ${selectedWeek}주차`}
                            {period === 'daily' && selectedDay != null && ` ${selectedDay}일`}
                        </span>
                    ) : isMonthOnly ? (
                        <span style={{ fontSize: 13, color: T.blue, fontWeight: 600, background: T.blueBg, padding: '3px 10px', borderRadius: 6 }}>
                            {`${baseDate.getFullYear()}년 ${baseDate.getMonth() + 1}월 기준`}
                            {isSameMonth(baseDate) && ' · 실시간'}
                        </span>
                    ) : (
                        <span style={{ fontSize: 13, color: T.text4, fontWeight: 500, background: T.inputBg, padding: '3px 10px', borderRadius: 6 }}>실시간</span>
                    )}
                </div>
                {(isPeriodFilterable || isMonthOnly) && (
                    <PeriodToggle
                        value={period}
                        onChange={handlePeriodChange}
                        loading={isFetching}
                        baseDate={baseDate}
                        onBaseDateChange={setBaseDate}
                        monthOnly={isMonthOnly}
                    />
                )}
            </div>

            {/* 카테고리 탭 */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {COACH_RANK_CATEGORIES.map((cat) => (
                    <button
                        key={cat.cat}
                        onClick={() => handleCatChange(cat.cat)}
                        style={{
                            padding: '6px 14px', borderRadius: 6,
                            border: activeCat === cat.cat ? `1px solid ${T.border}` : '1px solid transparent',
                            cursor: 'pointer', fontSize: 13, fontWeight: activeCat === cat.cat ? 700 : 500,
                            background: activeCat === cat.cat ? T.card : 'transparent',
                            color: activeCat === cat.cat ? T.navy : T.text3,
                        }}
                    >
                        {cat.cat}
                    </button>
                ))}
            </div>

            {/* 지표 버튼 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 2, background: T.inputBg, borderRadius: 8, padding: 2, border: `1px solid ${T.borderLight}` }}>
                    {currentCat.metrics.map((m) => (
                        <button
                            key={m.key}
                            onClick={() => setActiveMetric(m.key)}
                            style={{
                                padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                fontSize: 13, fontWeight: 600,
                                background: activeMetric === m.key ? T.card : 'transparent',
                                color: activeMetric === m.key ? T.navy : T.text3,
                                boxShadow: activeMetric === m.key ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                            }}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
                {!isPeriodFilterable && !isMonthOnly && (
                    <span style={{ fontSize: 13, color: T.text4, fontWeight: 500 }}>실시간 기준</span>
                )}
            </div>

            {/* 주별 서브탭 */}
            {isPeriodFilterable && period === 'weekly' && weeks.length > 0 && (
                <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
                    {weeks.map((w) => {
                        const isActive = selectedWeek === w.week;
                        return (
                            <button
                                key={w.week}
                                onClick={() => setSelectedWeek(isActive ? undefined : w.week)}
                                style={{
                                    padding: '5px 12px', borderRadius: 6, border: `1px solid ${isActive ? T.blue : T.border}`,
                                    cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                    background: isActive ? T.blueBg : 'transparent',
                                    color: isActive ? T.blue : T.text3,
                                }}
                            >
                                {w.label}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* 일별 서브탭 */}
            {isPeriodFilterable && period === 'daily' && (
                <div style={{ display: 'flex', gap: 3, marginBottom: 14, flexWrap: 'wrap' }}>
                    {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => {
                        const isActive = selectedDay === d;
                        return (
                            <button
                                key={d}
                                onClick={() => setSelectedDay(isActive ? undefined : d)}
                                style={{
                                    padding: '4px 8px', borderRadius: 6, border: `1px solid ${isActive ? T.blue : T.border}`,
                                    cursor: 'pointer', fontSize: 13, fontWeight: isActive ? 700 : 500,
                                    background: isActive ? T.blueBg : 'transparent',
                                    color: isActive ? T.blue : T.text3,
                                    minWidth: 28, textAlign: 'center',
                                }}
                            >
                                {d}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* 강사 없음 */}
            {sorted.length === 0 && (
                <div style={{ padding: '40px 0', textAlign: 'center', color: T.text3, fontSize: 15 }}>
                    {isFetching ? '불러오는 중...' : '등록된 강사가 없어요'}
                </div>
            )}

            {/* 바 차트 */}
            {sorted.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr', gap: '8px 10px', alignItems: 'center' }}>
                    {displayed.map((item, i) => {
                        // val: 바 width 계산용 (null → 0). displayVal: 화면 표시용 (null → '-').
                        const rawVal = item[activeMetric];
                        const val = typeof rawVal === 'number' && !isNaN(rawVal) ? rawVal : 0;
                        const displayVal = fmtVal(rawVal as number | null, currentMetricConf?.unit, activeMetric);
                        const color = COLORS[i % COLORS.length];
                        const journalBreakdown = item.journalWriteBreakdown;
                        const baseTooltip =
                            activeMetric === 'reRegistrationRate'
                                ? `재등록 ${item.reRegisteredMemberCount ?? 0}명 / 활성 ${item.activeMemberCount ?? 0}명 = ${displayVal}`
                                : activeMetric === 'reRegistrationSuccessRate'
                                ? `${RE_REGISTRATION_SUCCESS_NAMES.numerator} ${item.reRegistrationSucceededMemberCount ?? 0}명 / ${RE_REGISTRATION_SUCCESS_NAMES.denominator} ${item.reRegistrationEndedMemberCount ?? 0}명 = ${displayVal}`
                                : activeMetric === 'journalWriteRate' && journalBreakdown
                                ? formatJournalWriteBreakdownTooltip(journalBreakdown, displayVal)
                                : `${currentMetricConf?.label} · ${displayVal}`;
                        const tooltipText = canOpenEvidence
                            ? `${baseTooltip} · 클릭해 원천 데이터 보기`
                            : `${baseTooltip} · ${evidenceBlockReason}`;
                        const isHovered = hoveredCoachId === item.coachId;
                        const ratio = ratioOf(item);
                        const rank = metricRanking.get(item.coachId);
                        return (
                            <React.Fragment key={item.coachId}>
                                {/* 값이 없는 행에는 등수를 붙이지 않는다. 자리는 남겨 격자를 지킨다 */}
                                {rank != null ? (
                                    <RankBadge rank={rank} />
                                ) : (
                                    <span style={{ width: 28, display: 'inline-block' }} />
                                )}
                                <div style={{ whiteSpace: 'nowrap' }}>
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/centerReport/${item.coachId}`)}
                                        style={{ padding: 0, border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}
                                    >
                                        <span style={{ fontSize: 13, color: T.blue, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2 }}>
                                            {item.name}
                                        </span>
                                    </button>
                                </div>
                                <div
                                    role={canOpenEvidence ? 'button' : undefined}
                                    tabIndex={canOpenEvidence ? 0 : undefined}
                                    aria-label={canOpenEvidence ? `${item.name} ${currentMetricConf?.label} 원천 데이터 보기` : undefined}
                                    onMouseEnter={() => setHoveredCoachId(item.coachId)}
                                    onMouseLeave={() => setHoveredCoachId((prev) => (prev === item.coachId ? null : prev))}
                                    onClick={() => {
                                        if (!canOpenEvidence) return;
                                        setEvidenceTarget({ coachId: item.coachId, name: item.name });
                                    }}
                                    onKeyDown={(e) => {
                                        if (!canOpenEvidence) return;
                                        if (e.key !== 'Enter' && e.key !== ' ') return;
                                        e.preventDefault();
                                        setEvidenceTarget({ coachId: item.coachId, name: item.name });
                                    }}
                                    style={{
                                        height: 28, background: T.inputBg, borderRadius: 6, position: 'relative',
                                        border: `1px solid ${isHovered && canOpenEvidence ? T.blueBorder : T.borderLight}`,
                                        cursor: canOpenEvidence ? 'pointer' : 'default',
                                    }}
                                >
                                    <div
                                        style={{
                                            height: '100%', borderRadius: 6,
                                            /* 🔴 100% 클램프 — 기준이 틀려도 막대가 화면 밖으로 뻗지 않는다 */
                                            width: `${maxV ? Math.min(Math.max((val / maxV) * 100, 0), 100) : 0}%`,
                                            background: `linear-gradient(90deg,${color}30,${color}90)`,
                                            transition: 'width 0.4s ease',
                                        }}
                                    />
                                    <span style={{ position: 'absolute', right: 12, top: 5, fontSize: 13, color: T.text1, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                        {ratio && (
                                            <span style={{ fontSize: 11, color: T.text3, fontWeight: 600, marginRight: 7 }}>{ratio}</span>
                                        )}
                                        {displayVal}
                                    </span>
                                    {isHovered && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                bottom: 'calc(100% + 6px)',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                background: T.navy,
                                                color: '#fff',
                                                padding: '6px 10px',
                                                borderRadius: 6,
                                                fontSize: 12,
                                                fontWeight: 600,
                                                whiteSpace: 'nowrap',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                                                pointerEvents: 'none',
                                                zIndex: 10,
                                            }}
                                        >
                                            {tooltipText}
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    width: 0,
                                                    height: 0,
                                                    borderLeft: '5px solid transparent',
                                                    borderRight: '5px solid transparent',
                                                    borderTop: `5px solid ${T.navy}`,
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>
            )}

            {/* 더보기/접기 */}
            {sorted.length > 5 && (
                <button
                    onClick={() => setExpanded((e) => !e)}
                    style={{
                        marginTop: 16, width: '100%', padding: '8px 0', borderRadius: 8,
                        border: `1px solid ${T.border}`, background: T.inputBg,
                        cursor: 'pointer', fontSize: 13, fontWeight: 600, color: T.text2,
                    }}
                >
                    {expanded ? '▲ 접기' : `▼ 전체 보기 (${sorted.length}명)`}
                </button>
            )}

            {/*
              🔴 등수가 사라진 이유를 밝힌다. 아무 말 없이 배지만 없으면
                 원장은 화면이 고장 난 줄 안다 — 값 칸도 전부 '—' 라 더 그렇다.
            */}
            {sorted.length > 0 && activeMetric === 'reRegistrationSuccessRate' && conversionDegraded && (
                <p style={{ marginTop: 12, fontSize: 12, color: T.text4 }}>
                    이 달 {RE_REGISTRATION_SUCCESS_NAMES.label} 집계가 <b>실패했어요.</b> 값과 순위를 낼 수
                    없어 비워 뒀습니다 — 0% 가 아닙니다. 잠시 후 다시 불러와 주세요.
                </p>
            )}

            {sorted.length > 0 && (
                <p style={{ marginTop: 12, fontSize: 12, color: T.text4 }}>
                    {canOpenEvidence
                        ? '막대를 클릭하면 이 숫자를 이루는 회원·수업·결제 건을 하나씩 볼 수 있어요.'
                        : evidenceBlockReason}
                </p>
            )}

            {evidenceTarget && (
                <CoachMetricEvidenceModal
                    centerId={centerId}
                    coachId={evidenceTarget.coachId}
                    coachName={evidenceTarget.name}
                    metricKey={activeMetric}
                    month={baseDateParam}
                    onClose={() => setEvidenceTarget(null)}
                />
            )}
        </div>
    );
};

export default FeedCenterStatisticsRanking;
